import { useEffect, useState } from 'react'
import { ConsignmentItem } from '../models/types'
import { ConsignmentItemPatch } from '../models/request'

export type EditableConsignmentItemField =
  | 'commodityDescriptionOfGoods'
  | 'goodsMeasureGrossMass'
  | 'goodsMeasureNetMass'
  | 'totalNumberOfPackages'

export type InlineEditField = EditableConsignmentItemField | 'commodityCode'

type InlineValues = {
  commodityDescriptionOfGoods: string
  goodsMeasureGrossMass: string
  goodsMeasureNetMass: string
  totalNumberOfPackages: string
  commodityHarmonizedSystemSubHeadingCode: string
  commodityCombinedNomenclatureCode: string
}

const toValues = (consignmentItem: ConsignmentItem): InlineValues => ({
  commodityDescriptionOfGoods: consignmentItem.commodityDescriptionOfGoods ?? '',
  goodsMeasureGrossMass: consignmentItem.goodsMeasureGrossMass?.toString() ?? '',
  goodsMeasureNetMass: consignmentItem.goodsMeasureNetMass?.toString() ?? '',
  totalNumberOfPackages: consignmentItem.totalNumberOfPackages?.toString() ?? '',
  commodityHarmonizedSystemSubHeadingCode: consignmentItem.commodityHarmonizedSystemSubHeadingCode ?? '',
  commodityCombinedNomenclatureCode: consignmentItem.commodityCombinedNomenclatureCode ?? '',
})

const toPatch = (field: EditableConsignmentItemField, value: string): ConsignmentItemPatch => {
  if (field === 'commodityDescriptionOfGoods') {
    return { commodityDescriptionOfGoods: value }
  }
  return { [field]: Number(value) }
}

// Masses and package count must never be null on the server — an emptied input saves as 0.
const normalizeDraft = (field: EditableConsignmentItemField, value: string): string => {
  if (field === 'commodityDescriptionOfGoods') {
    return value
  }
  return value.trim() === '' ? '0' : value
}

export default function useConsignmentItemInlineEdit(
  consignmentItem: ConsignmentItem,
  onSave?: (patch: ConsignmentItemPatch) => Promise<void>,
) {
  const [editingField, setEditingField] = useState<InlineEditField | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [values, setValues] = useState<InlineValues>(() => toValues(consignmentItem))
  const [draft, setDraft] = useState('')

  useEffect(() => {
    setValues(toValues(consignmentItem))
  }, [consignmentItem])

  const startEditing = (field: InlineEditField) => {
    if (field !== 'commodityCode') {
      setDraft(values[field])
    }
    setEditingField(field)
  }

  const cancelEditing = () => {
    setEditingField(null)
  }

  const persist = async (patch: ConsignmentItemPatch, optimistic: Partial<InlineValues>) => {
    if (!onSave) {
      setValues((previous) => ({ ...previous, ...optimistic }))
      setEditingField(null)
      return
    }

    setIsSaving(true)
    try {
      await onSave(patch)
      setValues((previous) => ({ ...previous, ...optimistic }))
      setEditingField(null)
    } catch {
      // Stay in edit mode so the user can retry or cancel; the caller surfaces
      // the error to the user.
    } finally {
      setIsSaving(false)
    }
  }

  const saveEditing = async () => {
    if (editingField === null || editingField === 'commodityCode') {
      return
    }
    const field = editingField
    const normalizedDraft = normalizeDraft(field, draft)
    await persist(toPatch(field, normalizedDraft), { [field]: normalizedDraft })
  }

  const saveCommodityCode = async (
    harmonizedSystemSubHeadingCode: string,
    combinedNomenclatureCode: string,
  ) => {
    await persist(
      {
        commodityHarmonizedSystemSubHeadingCode: harmonizedSystemSubHeadingCode,
        commodityCombinedNomenclatureCode: combinedNomenclatureCode,
      },
      {
        commodityHarmonizedSystemSubHeadingCode: harmonizedSystemSubHeadingCode,
        commodityCombinedNomenclatureCode: combinedNomenclatureCode,
      },
    )
  }

  return {
    editingField,
    isEditing: editingField !== null,
    isSaving,
    values,
    draft,
    setDraft,
    startEditing,
    cancelEditing,
    saveEditing,
    saveCommodityCode,
  }
}
